/**
 * Types + pure logic for the Ads pool analytics tab.
 *
 * Row shapes mirror the RPC return columns documented at the top of
 * supabase/migrations/20260924200200_ads_pool_analytics.sql. bigint / numeric arrive from PostgREST as
 * numbers or numeric strings, so every read goes through num() / n0().
 */
import { Check, n0, num } from '../tracking-pool/format';

export interface AdsPoolSummary {
    window_start: string;
    window_end: string;
    paid_sessions: number;
    paid_sessions_prev: number;
    sessions_shown: number;
    pool_calls: number;
    pool_calls_prev: number;
    pool_first_time_calls: number;
    pool_linked_calls: number;
    legacy_clean: number;
    legacy_ambiguous: number;
    legacy_after_hold: number;
    legacy_none: number;
    ambiguous_pct: number | null;
    after_hold_pct: number | null;
    pool_qualified: number;
    pool_qualified_gclid: number;
    gclid_attach_pct: number | null;
    cohort_leads: number;
    cohort_qualified: number;
    cohort_cases: number;
    cohort_signed: number;
    cohort_won: number;
    cohort_revenue_cents: number;
    cohort_won_prev: number;
    cohort_revenue_cents_prev: number;
    uploads_qualified: number;
    uploads_sent: number;
    uploads_failed: number;
    uploads_pending: number;
    shadow_live: boolean;
    shadow_calls: number | null;
    shadow_matched: number | null;
    shadow_ambiguous: number | null;
    shadow_unmatched: number | null;
    pool_live: boolean;
    pool_matched_calls: number;
    pool_unmatched_calls: number;
    claims: number;
    claims_blocked: number;
    claims_overflow: number;
    blocking_pct: number | null;
    overflow_pct: number | null;
}

export interface AdsCoverageWeek {
    week_start: string;
    pool_clean: number;
    pool_ambiguous: number;
    pool_after_hold: number;
    pool_none: number;
    pool_new_matched: number;
    pool_new_unmatched: number;
    call_asset: number;
    call_asset_with_campaign: number;
    gbp_montreal_ad: number;
    gbp_montreal_maps: number;
    gbp_montreal_unsplit: number;
    gbp_toronto_ad: number;
    gbp_toronto_maps: number;
    gbp_toronto_unsplit: number;
    total: number;
    first_time_total: number;
    is_partial: boolean;
    /** One entry per site_phone_lines paid_* row (default / pool-full, active or retired), zero-filled, labelled by paid_line_label(). */
    configured_lines: ConfiguredLineWeek[];
}

/** A paid default / pool-full number: a site_phone_lines paid_* row (T-006); label from paid_line_label() (T-024). */
export interface ConfiguredLineWeek {
    line: string;
    label: string;
    market: string | null;
    role: 'default' | 'pool_full';
    e164: string;
    active: boolean;
    calls: number;
}

export interface AdsArmRow {
    arm: 'legacy' | 'shadow' | 'canary';
    available: boolean;
    calls: number;
    matched: number;
    ambiguous: number;
    after_hold: number;
    unmatched: number;
    matched_pct: number | null;
    ambiguous_pct: number | null;
    unmatched_pct: number | null;
    qualified: number;
    qualified_gclid: number;
    gclid_attach_pct: number | null;
    language_known: number;
    language_mismatch: number;
    mismatch_pct: number | null;
    agrees_with_legacy: number | null;
    different_gclid: number | null;
}

export interface AdsSuspect {
    twilio_call_sid: string;
    call_session_id: string | null;
    called_at: string;
    source: 'legacy' | 'pool';
    legacy_class: 'clean' | 'ambiguous' | 'after_hold' | 'none' | null;
    candidate_count: number;
    minutes_after_assignment: number | null;
    number_e164: string;
    market: string | null;
    landing_page: string | null;
    utm_campaign: string | null;
    implied_language: string | null;
    lead_id: string | null;
    lead_stage: string | null;
    lead_language: string | null;
    is_qualified: boolean;
    has_gclid: boolean;
    caller_area_code: string | null;
    posthog_distinct_id: string | null;
    reasons: string[];
    is_suspect: boolean;
}

export interface UploadLine {
    line: string;
    /** From paid_line_label() for site_phone_lines paid_* numbers ("paid · QC default", …); fixed for pool / GBP / web forms. */
    label: string;
    market: string | null;
    role: string;
    calls: number;
    first_time_calls: number;
    leads: number;
    qualified: number;
    qualified_gclid: number;
    qualified_ecl_only: number;
    sent: number;
    failed: number;
    pending: number;
    split_ad_calls: number;
    split_maps_calls: number;
    campaign_matched_calls: number;
}

export interface GoogleUploadStatus {
    conversion_action_id: number;
    conversion_action_name: string | null;
    client: string;
    status: string | null;
    successful_event_count: number | null;
    total_event_count: number | null;
    pending_event_count: number | null;
    failed_event_count: number | null;
    alerts: unknown[];
    last_upload_at: string | null;
    snapshot_date: string;
}

export interface UploadHealth {
    lines: UploadLine[];
    google: GoogleUploadStatus[];
    google_available: boolean;
    latest_snapshot: string | null;
}

export interface BreakdownRow {
    dim_key: string;
    dim_label: string;
    sessions: number;
    shown: number;
    calls: number;
    matched: number;
    leads: number;
    qualified: number;
    won: number;
    revenue_cents: number;
    clicks: number | null;
    cost_cents: number | null;
    cost_per_qualified_cents: number | null;
}

export interface AdsTrendWeek {
    week_start: string;
    pool_calls: number;
    clean: number;
    ambiguous: number;
    after_hold: number;
    unlinked: number;
    first_time: number;
    clean_pct: number | null;
    ambiguous_pct: number | null;
    pool_qualified: number;
    pool_qualified_gclid: number;
    gclid_attach_pct: number | null;
    ad_qualified: number;
    shadow_calls: number;
    shadow_matched: number;
    shadow_matched_pct: number | null;
    is_partial: boolean;
}

export type Market = 'all' | 'qc' | 'on';

export interface LegacyLoadHour {
    market: Market;
    hour_et: number;
    arrivals_per_hour: number;
    mean_in_use_legacy: number;
    max_in_use_legacy: number;
    mean_in_use_150: number;
    max_in_use_150: number;
}

export interface LegacyLoadMarket {
    market: Market;
    sessions: number;
    sessions_shown: number;
    assignments: number;
    linked_calls: number;
    calls_per_100_shown: number | null;
    qualified: number;
    qualified_per_100_shown: number | null;
    callers: number;
    ontario_area_callers: number;
    pool_size_now: number;
    pool_size_proposed: number;
}

/** Blocked paid claims by requested market + that market's pool-full number (Q16: shown only when both sub-pools are full). */
export interface PoolFullMarket {
    market: 'qc' | 'on';
    label: string | null;
    display: string | null;
    e164: string | null;
    active: boolean | null;
    claims: number;
    blocked: number;
    overflow: number;
    blocking_pct: number | null;
    overflow_pct: number | null;
    pool_full_calls: number;
}

export interface LegacyLoad {
    hourly: LegacyLoadHour[];
    markets: LegacyLoadMarket[];
    pool_full: PoolFullMarket[];
    stats: {
        assignments: number;
        assignments_unpaid: number;
        days: number;
        sessions_shown: number;
        sessions_two_numbers: number;
        fallback_assignments: number;
        delay_p50_min: number | null;
        delay_p75_min: number | null;
        delay_p90_min: number | null;
        delay_p95_min: number | null;
        legacy_numbers: number;
        hold_minutes: number;
        window_minutes_new: number;
    };
}

// ── labels ───────────────────────────────────────────────────────────────────

export const ADS_FUNNEL_LABEL: Record<string, string> = {
    clicks: 'Google Ads clicks',
    sessions: 'Paid sessions (with a gclid)',
    shown: 'Pool number shown',
    called: 'Called a pool number',
    matched: 'Matched to one visit, cleanly',
    leads: 'Leads from those clicks',
    qualified: 'Qualified',
    case: 'Case opened',
    signed: 'Contract signed',
    won: 'Won (signed + paid)',
};

/** Row label for a line: the RPC's label (paid_line_label() for site_phone_lines paid_* numbers). */
export const lineLabel = (l: { line: string; label?: string | null }): string => l.label ?? l.line;

/** The paid default / pool-full numbers the plan expects (§7d); a missing one is shown as "not bought yet". */
export const EXPECTED_LINES: { line: string; label: string }[] = [
    { line: 'default_qc', label: 'paid · QC default' },
    { line: 'pool_full_qc', label: 'paid · QC pool-full' },
    { line: 'default_on', label: 'paid · ON default' },
    { line: 'pool_full_on', label: 'paid · ON pool-full' },
];

export const DIM_LABEL: Record<string, string> = {
    campaign: 'Campaign',
    ad_group: 'Ad group',
    landing_page: 'Landing page',
    market: 'Market (page province)',
};

export const MARKET_LABEL: Record<Market, string> = { all: 'All', qc: 'Quebec / other pages', on: 'Ontario pages' };

// ── plan §11 thresholds ──────────────────────────────────────────────────────

/** Ontario calls per 100 shown under today's 438 numbers (plan §6a); guard ±15 % after the switch. */
export const ONTARIO_CALLS_BASELINE = 6.2;
/** Language-mismatch floor = the clean-match rate; suspects above it are the wrong-session signal. */
export const LANGUAGE_FLOOR_FALLBACK = 9;

const pct = (a: number, b: number): number | null => (b > 0 ? (100 * a) / b : null);

/** Mismatch rate among clean legacy matches with both languages known = the noise floor (plan §3: 9 %). */
export const languageFloor = (suspects: AdsSuspect[] | undefined): { floor: number | null; known: number; mismatched: number } => {
    const clean = (suspects ?? []).filter(s => s.legacy_class === 'clean' && s.implied_language && s.lead_language);
    const mm = clean.filter(s => s.reasons.includes('language_mismatch')).length;
    return { floor: pct(mm, clean.length), known: clean.length, mismatched: mm };
};

export interface MismatchByClass { cls: string; label: string; known: number; mismatched: number; rate: number | null }

export const mismatchByClass = (suspects: AdsSuspect[] | undefined): MismatchByClass[] => {
    const classes: [string, string][] = [
        ['clean', 'Clean — one visit, within the hold'],
        ['ambiguous', 'Ambiguous — 2+ candidate visits'],
        ['after_hold', 'After the 40-min hold'],
        ['pool', 'New pool — matched'],
    ];
    return classes.map(([cls, label]) => {
        const rows = (suspects ?? []).filter(s => (cls === 'pool' ? s.source === 'pool' : s.source === 'legacy' && s.legacy_class === cls)
            && s.implied_language && s.lead_language);
        const mm = rows.filter(s => s.reasons.includes('language_mismatch')).length;
        return { cls, label, known: rows.length, mismatched: mm, rate: pct(mm, rows.length) };
    }).filter(r => r.cls !== 'pool' || r.known > 0);
};

export interface AdsCheckInputs {
    summary: AdsPoolSummary | undefined;
    suspects: AdsSuspect[] | undefined;
    upload: UploadHealth | undefined;
    load: LegacyLoad | undefined;
    arms: AdsArmRow[] | undefined;
}

/**
 * Plan §11 "Is it working?". Until the canary runs, each check is judged on TODAY's path (legacy
 * matcher) — that column is the baseline the cutover is measured against, so red today is expected.
 * Checks that need stage-2 data (claims, the Ontario switch, calls/100 vs a canary arm) stay
 * 'waiting' and show today's baseline value instead of a verdict.
 */
export const evaluateAdsChecks = ({ summary: s, suspects, upload, load, arms }: AdsCheckInputs): Check[] => {
    const checks: Check[] = [];
    const wait = (key: string, label: string, target: string, value = '—'): Check => ({ key, label, target, status: 'waiting', value });
    const canary = arms?.find(a => a.arm === 'canary');
    const live = !!s?.pool_live && !!canary?.available;

    // 1. ambiguous
    const amb = live ? num(canary?.ambiguous_pct) : num(s?.ambiguous_pct);
    checks.push(amb == null ? wait('ambiguous', 'Ambiguous matches (2+ candidates)', '≤ 1 %')
        : { key: 'ambiguous', label: 'Ambiguous matches (2+ candidates)', target: '≤ 1 %', value: `${amb.toFixed(1)} %`,
            status: amb <= 1 ? 'pass' : amb <= 3 ? 'watch' : 'fail' });

    // 2. after hold / unmatched
    const ah = live ? num(canary?.unmatched_pct) : num(s?.after_hold_pct);
    checks.push(ah == null ? wait('unmatched', 'Called after the hold / unmatched', '≤ 8 %, 0 guessed')
        : { key: 'unmatched', label: live ? 'Unmatched (called after the window)' : 'Called after the hold (guessed today)',
            target: '≤ 8 %, 0 guessed', value: `${ah.toFixed(1)} %`,
            status: !live && n0(s?.legacy_after_hold) > 0 ? 'fail' : ah <= 8 ? 'pass' : ah <= 12 ? 'watch' : 'fail' });

    // 3. language mismatch vs the clean floor
    const floor = languageFloor(suspects).floor;
    const matchedRows = (suspects ?? []).filter(r => (live ? r.source === 'pool' : r.source === 'legacy') && r.implied_language && r.lead_language);
    const mmRate = pct(matchedRows.filter(r => r.reasons.includes('language_mismatch')).length, matchedRows.length);
    checks.push(floor == null || mmRate == null ? wait('mismatch', 'Language mismatch on matched calls', 'floor + 3 pts')
        : { key: 'mismatch', label: 'Language mismatch on matched calls', target: 'floor + 3 pts',
            value: `${mmRate.toFixed(0)} % (floor ${floor.toFixed(0)} %)`,
            status: mmRate - floor <= 3 ? 'pass' : mmRate - floor <= 5 ? 'watch' : 'fail' });

    // 4. gclid attach on pool-qualified
    const attach = num(s?.gclid_attach_pct);
    checks.push(attach == null ? wait('attach', 'gclid on pool-qualified leads', '≥ 88 %')
        : { key: 'attach', label: 'gclid on pool-qualified leads', target: '≥ 88 %',
            value: `${attach.toFixed(0)} % · ${n0(s?.pool_qualified_gclid)}/${n0(s?.pool_qualified)}`,
            status: attach >= 88 ? 'pass' : attach >= 85 ? 'watch' : 'fail' });

    // 5. upload failures (ours + Google status)
    const failed = n0(s?.uploads_failed);
    const google = upload?.google ?? [];
    const googleBad = google.filter(g => g.status && g.status !== 'EXCELLENT');
    checks.push({
        key: 'upload', label: 'Upload failures (ours) · Google status', target: '0 · EXCELLENT',
        value: `${failed} of ${n0(s?.uploads_qualified)} · ${upload?.google_available ? (googleBad.length ? googleBad.map(g => g.status).join(', ') : 'EXCELLENT') : 'Google status waiting'}`,
        status: failed === 0 && googleBad.length === 0 ? (upload?.google_available ? 'pass' : 'watch') : failed <= 1 && googleBad.length === 0 ? 'watch' : 'fail',
    });

    // 6. blocking / overflow (stage 2)
    const blk = num(s?.blocking_pct);
    const ovf = num(s?.overflow_pct);
    checks.push(!s?.pool_live || blk == null ? wait('blocking', 'Claims blocked (per market) · overflow', '< 1 % · < 3 %', 'stage 2 (canary)')
        : { key: 'blocking', label: 'Claims blocked (per market) · overflow', target: '< 1 % · < 3 %',
            value: `${blk.toFixed(1)} % · ${(ovf ?? 0).toFixed(1)} %`,
            status: blk < 1 && (ovf ?? 0) < 3 ? 'pass' : blk < 2 ? 'watch' : 'fail' });

    // 7. Ontario calls / 100 shown (baseline today; judged after the Ontario numbers ship)
    const on = load?.markets.find(m => m.market === 'on');
    const onRate = num(on?.calls_per_100_shown);
    checks.push(wait('ontario', 'Ontario calls / 100 shown', `${ONTARIO_CALLS_BASELINE} ± 15 %`,
        onRate == null ? 'stage 2 (Ontario numbers)' : `baseline ${onRate.toFixed(1)} · 438 numbers`));

    // 8. sessions holding more than one number
    const two = n0(load?.stats.sessions_two_numbers);
    const shown = n0(load?.stats.sessions_shown);
    const twoPct = pct(two, shown);
    checks.push(twoPct == null ? wait('two_numbers', 'Sessions holding 2+ numbers', '< 1 %')
        : { key: 'two_numbers', label: 'Sessions holding 2+ numbers', target: '< 1 %', value: `${twoPct.toFixed(1)} %`,
            status: twoPct < 1 ? 'pass' : twoPct < 3 ? 'watch' : 'fail' });

    // 9. paid calls / 100 sessions (vs the canary's legacy arm)
    const per100 = pct(n0(s?.pool_calls), n0(s?.paid_sessions));
    checks.push(wait('per100', 'Pool calls / 100 paid sessions', '± 15 % of baseline',
        per100 == null ? '—' : `baseline ${per100.toFixed(1)}`));

    return checks;
};
